import { endOfYear, format, startOfYear } from "date-fns";
import { AccumDateData } from "../../../employment/employmentApi";

export enum AccumulationCalculationType {
  MULTI_CALCULATION = "multiCalculation",
  SINGLE_CALCULATION = "singleCalculation",
  GROUP_MATH = "groupMath",
}

const getInternalStateAccumDefaultValues = (
  stateName: string,
  isMultiCalculation: boolean,
): AccumDateData => {
  if (isMultiCalculation) {
    const ongoingAverages: { [key: string]: any } = {};

    ongoingAverages["NO_DATE"] = {
      valueDate: "",
      salary: "",
      workTime: "",
    };

    return {
      date: null,
      rows: [
        {
          [stateName]: {
            ongoingAverages,
          },
        },
      ],
    };
  } else {
    const currentYear = new Date().getFullYear();
    const lastDay = format(
      endOfYear(new Date(currentYear, 0, 1)),
      "yyyy-MM-dd",
    );
    const firstDay = format(
      startOfYear(new Date(currentYear, 0, 1)),
      "yyyy-MM-dd",
    );

    const partitionKey = `${lastDay} ${firstDay} ${lastDay}`;

    return {
      date: null,
      rows: [
        {
          [stateName]: {
            partitionKey,
            workTime: "",
            completedLimits: "", // not shown in UI
          },
        },
      ],
    };
  }
};

const getGroupMathDefaultValues = (): AccumDateData[] => {
  const ongoingAverages: { [key: string]: any } = {};
  ongoingAverages["NO_DATE"] = {
    valueDate: "",
    salary: "",
    workTime: "",
  };

  const lomakeskiansiolaskentaDefaults: AccumDateData = {
    date: null,
    rows: [
      {
        lomakeskiansiolaskenta: {
          ongoingAverages,
        },
      },
    ],
  };

  // Default values for viikkokeskityömäärälaskenta (partitions)
  const currentYear = new Date().getFullYear();
  const lastDay = format(endOfYear(new Date(currentYear, 0, 1)), "yyyy-MM-dd");
  const firstDay = format(
    startOfYear(new Date(currentYear, 0, 1)),
    "yyyy-MM-dd",
  );

  const partitionKey = `${lastDay} ${firstDay} ${lastDay}`;

  const tyotuntienKeskimaaraDefaults: AccumDateData = {
    date: null,
    rows: [
      {
        viikkokeskityömäärälaskenta: {
          partitionKey,
          workTime: "",
          completedLimits: "",
        },
      },
    ],
  };

  return [lomakeskiansiolaskentaDefaults, tyotuntienKeskimaaraDefaults];
};

export const ACCUMULATION_OPTIONS = [
  {
    value: "Vuosilomakertymä (keskeneräinen)",
    stateNames: ["vuosilomakertymä"],
    displayNames: ["vuosilomapäivät (pv)"],
    scope: "EMPLOYMENT_PER_MONTH_AND_ASSIGNMENT",
    type: "employment",
    isInternal: false,
  },
  {
    value: "Vuosilomakertymä (valmis)",
    stateNames: ["vuosiloma"],
    displayNames: ["vuosilomapäivät (pv)"],
    scope: "EMPLOYMENT_PER_MONTH",
    type: "employment",
    isInternal: false,
  },
  {
    value: "Vuosilomapalkkakertymä (keskeneräinen)",
    stateNames: ["vuosilomapalkkakertymä"],
    displayNames: ["vuosilomapalkkakertymä (€)"],
    scope: "EMPLOYMENT_PER_MONTH_AND_ASSIGNMENT",
    type: "employment",
    isInternal: false,
  },
  {
    value: "Vuosilomapalkkakertymä (valmis)",
    stateNames: ["vuosilomapalkka"],
    displayNames: ["Vuosilomapalkka (€)"],
    scope: "EMPLOYMENT_PER_MONTH_AND_ASSIGNMENT",
    type: "employment",
    isInternal: false,
  },
  {
    value: "LomaKPA tai LomaKTA (keskeneräinen)",
    stateNames: ["lomakeskiansiolaskenta", "viikkokeskityömäärälaskenta"],
    displayNames: ["lomakeskiansiolaskenta", "työviikot"],
    scope: ["EMPLOYMENT_PER_ASSIGNMENT", "EMPLOYMENT"],
    type: "employment",
    isInternal: true,
    calculationType: AccumulationCalculationType.GROUP_MATH,
    defaultValues: getGroupMathDefaultValues(),

    // parseJsonProps: new Map([
    //   [
    //     "ansio (eur)",
    //     "kirjanpito_vuosilomapalkkavelka.ongoingAverages.*.salary",
    //   ],
    //   [
    //     "työaika ( h / pv )",
    //     "kirjanpito_vuosilomapalkkavelka.ongoingAverages.*.workTime",
    //   ],
    // ]),
  },
  {
    value: "Lomarahakertymä (keskeneräinen)",
    stateNames: ["lomarahakertymä"],
    displayNames: ["lomarahakertymä (€)"],
    scope: "EMPLOYMENT_PER_MONTH_AND_ASSIGNMENT",
    type: "employment",
    isInternal: false,
  },
  {
    value: "Lomarahakertymä (valmis)",
    stateNames: ["lomaraha"],
    displayNames: ["lomarahakertymä (€)"],
    scope: "EMPLOYMENT_PER_MONTH_AND_ASSIGNMENT",
    type: "employment",
    isInternal: false,
  },
  {
    value: "Lomarahapäivät (valmis)",
    stateNames: ["vuosilomarahapäivät"],
    displayNames: ["vuosilomarahapäivät"],
    scope: "EMPLOYMENT_PER_MONTH",
    type: "employment",
    isInternal: false,
  },
  {
    value: "KTA/KPA (valmis)",
    stateNames: ["keskiansio"],
    displayNames: ["KTA/KPA valmis (€)"],
    scope: "EMPLOYMENT",
    type: "employment",
    isInternal: false,
  },
  {
    value: "KTA/KPA (keskeneräinen)",
    stateNames: ["keskiansiolaskenta"],
    displayNames: ["keskiansiolaskenta"],
    scope: "EMPLOYMENT",
    type: "employment",
    isInternal: true,
    calculationType: AccumulationCalculationType.MULTI_CALCULATION,
    defaultValues: getInternalStateAccumDefaultValues(
      "keskiansiolaskenta",
      true,
    ),
    // parseJsonProps: new Map([
    //   ["ansio (eur)", "keskiansiolaskenta.ongoingAverages.*.salary"],
    //   ["työaika ( h / pv )", "keskiansiolaskenta.ongoingAverages.*.workTime"],
    // ]),
  },
  {
    value: "Työajanlyhennystunnit (käytössä olevat)",
    stateNames: ["työajanlyhennys"],
    displayNames: ["työajanlyhennys (h)"],
    scope: "EMPLOYMENT_PER_YEAR",
    type: "employment",
    isInternal: false,
  },
  {
    value: "Työajanlyhennyslaskenta",
    stateNames: ["työajanlyhennyslaskenta"],
    displayNames: ["työajanlyhennyslaskenta"],
    scope: "EMPLOYMENT",
    type: "employment",
    isInternal: true,
    calculationType: AccumulationCalculationType.SINGLE_CALCULATION,
    defaultValues: getInternalStateAccumDefaultValues(
      "työajanlyhennyslaskenta",
      false,
    ),
    // parseJsonProps: new Map([
    //   ["kerryttävä työaika (h/pv/vko)", "työajanlyhennyslaskenta.workTime"],
    // ]),
  },
  {
    value: "Vuosivapaapäivät (käytössä)",
    stateNames: ["vuosivapaa"],
    displayNames: ["vuosivapaa (pv)"],
    scope: "CONTINUOUS_EMPLOYMENT_PER_YEAR",
    type: "continuousEmployment",
    isInternal: false,
  },
  {
    value: "Vuosivapaalaskenta",
    stateNames: ["vuosivapaalaskenta"],
    displayNames: ["vuosivapaalaskenta"],
    scope: "CONTINUOUS_EMPLOYMENT",
    type: "continuousEmployment",
    isInternal: true,
    calculationType: AccumulationCalculationType.SINGLE_CALCULATION,
    defaultValues: getInternalStateAccumDefaultValues(
      "vuosivapaalaskenta",
      false,
    ),
    // parseJsonProps: new Map([
    //   ["kerryttävä työaika (h)", "vuosivapaalaskenta.workTime"],
    // ]),
  },
  {
    value: "AP-vapaa (HuviTES)",
    stateNames: ["ap-vapaa"],
    displayNames: ["ap-vapaa (h)"],
    scope: "CONTINUOUS_EMPLOYMENT_PER_YEAR",
    type: "continuousEmployment",
    isInternal: false,
  },
  {
    value: "Työaikapankki",
    stateNames: ["tyoaikapankki", "tyoaikapankki laskuttamatta"],
    displayNames: ["työaikapankki (h)", "työaikapankki laskuttamatta (h)"],
    scope: "EMPLOYMENT_PER_ASSIGNMENT",
    type: "employment",
    isInternal: false,
  },
  {
    value: "Liukumasaldo",
    stateNames: ["liukumasaldo", "liukumasaldo laskuttamatta"],
    displayNames: ["liukumasaldo (h)", "liukumasaldo laskuttamatta (h)"],
    scope: "EMPLOYMENT_PER_ASSIGNMENT",
    type: "employment",
    isInternal: false,
  },
  {
    value: "Vuosityöaikajärjestelmän saldo",
    stateNames: ["vuosityoaikajarjestelman_saldo"],
    displayNames: ["vuosityöaikajärjestelmän saldo (h)"],
    scope: "EMPLOYMENT_PER_ASSIGNMENT",
    type: "employment",
    isInternal: false,
  },
];
